import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Tabs, Tab, Badge, Box } from "@mui/material";
import { usePermissions } from "../../../context/permissionContext";
import { validatePermission } from "../../../utils/permissionUtils";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import CampaignListPage from "../CampaignListPage";

/**
 * Review Hub — Tabbed interface merging Initial Approval + For Review
 *
 * Tab 1 "New Submissions": campaigns with campaign_status=1 (first-time review)
 * Tab 2 "Pending Edits": campaigns with isForAdminReview=1 (any status)
 *
 * Tabs are permission-gated: each only renders if the user has the
 * corresponding permission. Users with both permissions see both tabs.
 */
const ReviewHub = () => {
  const { permissions, permissionsMapping } = usePermissions();
  const canInitialApproval = validatePermission(
    "Campaign - Initial Approval:View", permissions, permissionsMapping
  );
  const canForReview = validatePermission(
    "Campaign - For Review:View", permissions, permissionsMapping
  );

  const tabs = useMemo(() => {
    const t = [];
    if (canInitialApproval) {
      t.push({ id: "initial-approval", label: "New Submissions", countKey: "newSubmissions", color: "warning" });
    }
    if (canForReview) {
      t.push({ id: "for-review", label: "Pending Edits", countKey: "pendingEdits", color: "info" });
    }
    return t;
  }, [canInitialApproval, canForReview]);

  const [activeTab, setActiveTab] = useState(0);
  const [counts, setCounts] = useState({
    newSubmissions: 0,
    pendingEdits: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    axios
      .get(process.env.REACT_APP_API + "get-campaign-count", {
        headers: { "x-access-token": token },
      })
      .then((response) => {
        const data = response.data?.data?.campaign_count;
        if (data) {
          setCounts({
            newSubmissions: data.in_initial_approval_campaign || 0,
            pendingEdits: data.in_for_review_campaign || 0,
          });
        }
      })
      .catch((err) => {
        console.error("ReviewHub: failed to fetch counts", err?.message);
      });
  }, [activeTab]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  if (tabs.length === 0) return null;

  const currentTab = tabs[activeTab] || tabs[0];

  return (
    <PageShell>
      <PageHead title="Review hub" />
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              overflow: "visible",
              minHeight: 48,
              paddingRight: "24px",
            },
            "& .MuiTabs-scroller": { overflow: "visible !important" },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              label={
                <Badge
                  badgeContent={counts[tab.countKey]}
                  color={tab.color}
                  max={999}
                  sx={{ "& .MuiBadge-badge": { right: -16, top: -2 } }}
                >
                  {tab.label}
                </Badge>
              }
            />
          ))}
        </Tabs>
      </Box>

      <CampaignListPage statusKey={currentTab.id} />
    </PageShell>
  );
};

export default ReviewHub;
